import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const aiWRekrutacjiIt: Article = {
  slug: "ai-w-rekrutacji-it",
  updated: "2026-09-10",
  updatedLabel: {
    pl: "Aktualizacja: 10 września 2026",
    en: "Last updated: 10 September 2026",
    zh: "最近更新：2026 年 9 月 10 日",
  },
  topic: { pl: "AI w rekrutacji", en: "AI in hiring", zh: "AI 招聘" },
  pl: {
    title: "AI w rekrutacji IT: co działa, a co jest ryzykiem",
    h1: "AI w rekrutacji IT 2026: co działa, co zawodzi, co jest zabronione",
    metaTitle: "AI w rekrutacji IT 2026: zastosowania, ryzyka, AI Act",
    metaDescription:
      "Jak modele językowe zmieniają rekrutację IT w 2026: gdzie AI skraca proces, gdzie generuje błędy, co mówi AI Act o systemach wysokiego ryzyka i jak kandydaci używają AI.",
    eyebrow: "Insights · AI w rekrutacji",
    summary:
      "Gdzie modele językowe realnie skracają rekrutację IT, gdzie mylą się kosztownie i jakie zasady narzuca AI Act.",
    lead:
      "Rekrutacja IT w 2026 toczy się po obu stronach z udziałem modeli językowych: my używamy ich do researchu i porządkowania danych, kandydaci do pisania CV i przygotowania do rozmów. Prowadzimy procesy z Krakowa na całą Polskę i region CEE, więc opisujemy to bez marketingu: co u nas działa, czego świadomie nie automatyzujemy i gdzie prawo stawia twardą granicę.",
    note:
      "Tekst dotyczy stanu na wrzesień 2026. Zasady AI Act przywołujemy w ujęciu praktycznym dla pracodawcy; to nie jest porada prawna.",
    readTime: "10 min czytania",
    takeaways: [
      "AI dobrze radzi sobie z porządkowaniem danych: streszczaniem CV, mapowaniem technologii, przygotowaniem pytań. Źle radzi sobie z oceną, kogo zatrudnić.",
      "Systemy AI używane do selekcji kandydatów są w AI Act traktowane jako wysokiego ryzyka - wymagają nadzoru człowieka, dokumentacji i informowania kandydata.",
      "Automatyczne odrzucanie kandydatów przez model bez udziału człowieka to jednocześnie ryzyko prawne (RODO, art. 22) i rekrutacyjne.",
      "Modele językowe zawyżają dopasowanie kandydatów o rzadkich profilach, bo oceniają podobieństwo słów, nie umiejętności - stąd wysoki odsetek fałszywych trafień.",
      "Ponad połowa CV, które dziś dostajemy, przechodzi przez model językowy; klasyczna ocena „ładnie napisanego CV” straciła wartość predykcyjną.",
      "Nasze podejście: AI skraca research i przygotowanie, decyzję o rekomendacji zawsze podejmuje człowiek.",
    ],
    sections: [
      {
        id: "gdzie-dziala",
        eyebrow: "01 / Co działa",
        title: "Gdzie modele językowe realnie pomagają w rekrutacji IT",
        blocks: [
          {
            kind: "table",
            caption: "Zastosowania AI w procesie rekrutacyjnym - ocena praktyczna",
            head: ["Zastosowanie", "Wartość", "Warunek bezpiecznego użycia"],
            rows: [
              ["Streszczanie i porządkowanie CV", "Wysoka", "Człowiek czyta oryginał przed decyzją"],
              ["Mapowanie technologii i synonimów", "Wysoka", "Weryfikacja przez rekrutera technicznego"],
              ["Przygotowanie pytań na rozmowę", "Wysoka", "Pytania kalibrowane z zespołem klienta"],
              ["Redakcja ogłoszenia i wiadomości", "Średnia", "Konkret i widełki wpisuje człowiek"],
              ["Wstępne dopasowanie do wymagań", "Średnia", "Jako podpowiedź kolejności, nigdy jako filtr odrzucający"],
              ["Ocena kompetencji i decyzja o odrzuceniu", "Niska i ryzykowna", "Nie automatyzujemy - decyzja człowieka"],
              ["Analiza mimiki, głosu, „dopasowania kulturowego”", "Brak podstaw", "Odradzamy; wysokie ryzyko prawne i dyskryminacyjne"],
            ],
          },
          {
            kind: "p",
            text: "Prosty podział: model dobrze porządkuje informacje, które już istnieją, a źle tworzy oceny, których nikt nie zweryfikował. Wszystko, co przesuwa kandydata dalej lub go wyklucza, zostaje po stronie człowieka.",
          },
        ],
      },
      {
        id: "gdzie-zawodzi",
        eyebrow: "02 / Co zawodzi",
        title: "Pięć błędów, które modele językowe popełniają w ocenie kandydatów",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Podobieństwo słów zamiast umiejętności",
                meta: "Fałszywe trafienia",
                body: "Model nagradza CV, które powtarza słownictwo z ogłoszenia. Osoba, która opisała ten sam projekt innym językiem, spada w rankingu mimo lepszego doświadczenia.",
              },
              {
                title: "Karanie nietypowych ścieżek",
                meta: "Fałszywe odrzucenia",
                body: "Przebranżowienie, przerwa w karierze, praca w mniej znanej firmie - wszystko, co odbiega od wzorca, obniża ocenę bez merytorycznego powodu.",
              },
              {
                title: "Konfabulacja szczegółów",
                meta: "Ryzyko wiarygodności",
                body: "Streszczenie potrafi dopisać technologię lub rolę, której w CV nie było. Bez porównania z oryginałem trafia to potem do rekomendacji dla klienta.",
              },
              {
                title: "Brak kontekstu rynkowego",
                meta: "Złe widełki",
                body: "Model nie wie, ile realnie kosztuje senior w Krakowie w tym kwartale i jak duża jest dostępna pula. To wiedza z procesów, nie z tekstu.",
              },
              {
                title: "Powielanie uprzedzeń z danych",
                meta: "Ryzyko dyskryminacji",
                body: "Model uczony na historycznych decyzjach potrafi odtwarzać ich skrzywienia, w tym te dotyczące płci, wieku czy pochodzenia.",
              },
            ],
          },
          {
            kind: "p",
            text: "Żaden z tych błędów nie jest teoretyczny. Wszystkie kończą się tak samo: dobry kandydat nie trafia na krótką listę, a firma nigdy się o tym nie dowiaduje, bo w statystykach widać tylko tych, którzy przeszli dalej.",
          },
        ],
      },
      {
        id: "prawo",
        eyebrow: "03 / Prawo",
        title: "AI Act i RODO: co wolno, a czego nie w rekrutacji",
        blocks: [
          {
            kind: "table",
            caption: "Obowiązki pracodawcy korzystającego z AI w rekrutacji",
            head: ["Obszar", "Zasada", "Co to znaczy w praktyce"],
            rows: [
              ["Klasyfikacja systemu", "Selekcja kandydatów to zastosowanie wysokiego ryzyka wg AI Act", "Potrzebna dokumentacja, ocena ryzyka i rejestr użycia"],
              ["Nadzór człowieka", "Wymagany realny nadzór, nie formalne kliknięcie", "Osoba z kompetencjami może zmienić lub odrzucić wynik modelu"],
              ["Informowanie kandydata", "Kandydat wie, że w procesie użyto systemu AI", "Krótka, zrozumiała informacja w ogłoszeniu lub w pierwszym kontakcie"],
              ["Decyzje automatyczne", "RODO ogranicza decyzje wyłącznie automatyczne wywołujące skutki prawne", "Odrzucenie bez udziału człowieka jest ryzykowne prawnie"],
              ["Dane kandydatów", "Minimalizacja i podstawa prawna przetwarzania", "Nie wklejaj CV do publicznych narzędzi bez umowy powierzenia"],
              ["Emocje i biometria", "Rozpoznawanie emocji w rekrutacji jest zakazane", "Analiza mimiki czy tonu głosu w rozmowie jest niedopuszczalna"],
            ],
          },
          {
            kind: "p",
            text: "Najprostszy test zgodności: czy potrafisz wskazać osobę, która podjęła decyzję, i wyjaśnić kandydatowi jej powód w dwóch zdaniach. Jeśli odpowiedź brzmi „tak zdecydował system”, proces wymaga poprawy, niezależnie od tego, jak dobre są wyniki modelu.",
          },
        ],
      },
      {
        id: "kandydaci",
        eyebrow: "04 / Druga strona",
        title: "Kandydaci też używają AI - i to zmienia proces",
        blocks: [
          {
            kind: "list",
            items: [
              "CV pisane z pomocą modelu są poprawne językowo i dopasowane do ogłoszenia, więc samo „dobre CV” przestało odróżniać kandydatów.",
              "Listy motywacyjne straciły wartość informacyjną; zastępujemy je jednym konkretnym pytaniem o doświadczenie z projektu.",
              "Zadania domowe rozwiązywane w domu bez nadzoru mówią dziś więcej o narzędziu niż o kandydacie.",
              "Rozmowa o realnych decyzjach projektowych („dlaczego wybraliście to rozwiązanie, co poszło źle”) pozostaje najtrudniejsza do podrobienia.",
              "Zakaz używania AI w procesie jest niewykonalny; sensowniejsze jest jasne określenie, na którym etapie jest dozwolone, a gdzie sprawdzamy samodzielne myślenie.",
            ],
          },
          {
            kind: "p",
            text: "W praktyce przenieśliśmy ciężar oceny z artefaktów (CV, zadanie domowe) na rozmowę o konkretnych decyzjach i ich konsekwencjach. To jedyny etap, w którym widać różnicę między doświadczeniem a dobrze sformułowanym opisem doświadczenia.",
          },
        ],
      },
      {
        id: "nasze-podejscie",
        eyebrow: "05 / Nasze podejście",
        title: "Jak używamy AI w Fit-to-Win Sprint",
        blocks: [
          {
            kind: "table",
            caption: "Podział pracy między model a człowieka",
            head: ["Etap sprintu", "Rola AI", "Rola człowieka"],
            rows: [
              ["Mapa rynku", "Grupowanie profili po technologiach i seniority", "Wybór firm i segmentów, weryfikacja trafności"],
              ["Wiadomość do kandydata", "Wariant roboczy", "Kontekst projektu, widełki, podpis konkretnej osoby"],
              ["Screening", "Streszczenie rozmowy i notatek", "Ocena motywacji, dopasowania i realnych kompetencji"],
              ["Rekomendacja dla klienta", "Porządkowanie faktów", "Decyzja o rekomendacji i uzasadnienie"],
              ["Feedback dla kandydata", "Szkic", "Treść i wysyłka - zawsze od człowieka"],
            ],
          },
          {
            kind: "p",
            text: "Efekt jest mierzalny po stronie czasu, nie po stronie oceny: research, który zajmował dwa dni, zajmuje pół dnia, dzięki czemu krótka lista jest gotowa w 14 dni. Kryteria rekomendacji zostały te same i nadal odpowiada za nie osoba, która rozmawiała z kandydatem.",
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania",
    faq: [
      {
        q: "Czy AI może samodzielnie odrzucać kandydatów?",
        a: "Nie powinna. AI Act traktuje selekcję kandydatów jako zastosowanie wysokiego ryzyka i wymaga realnego nadzoru człowieka, a RODO ogranicza decyzje wyłącznie automatyczne wywołujące istotne skutki dla osoby. Odrzucenie musi mieć człowieka, który je zatwierdza i potrafi uzasadnić.",
      },
      {
        q: "Czy trzeba informować kandydata, że w rekrutacji używa się AI?",
        a: "Tak. Kandydat powinien wiedzieć, że w procesie działa system AI, na jakim etapie i jaki ma wpływ na decyzję. Wystarczy krótka, zrozumiała informacja w ogłoszeniu lub w pierwszym kontakcie.",
      },
      {
        q: "Czy analiza mimiki lub głosu na rozmowie jest dozwolona?",
        a: "Rozpoznawanie emocji w miejscu pracy i w rekrutacji jest zakazane w Unii Europejskiej. Niezależnie od prawa, takie narzędzia nie mają rzetelnych podstaw naukowych do oceny kompetencji.",
      },
      {
        q: "Czy CV napisane z pomocą AI to sygnał ostrzegawczy?",
        a: "Nie. Dziś to norma, a nie wyjątek. Ważne jest, czy kandydat potrafi opowiedzieć o opisanych projektach: jakie podjął decyzje, co nie wyszło i co zrobiłby inaczej.",
      },
      {
        q: "Czy AI skraca czas rekrutacji IT?",
        a: "Tak, ale w konkretnych miejscach: research, porządkowanie danych i przygotowanie materiałów. Największe oszczędności czasu nadal daje uporządkowany proces decyzyjny po stronie firmy, nie narzędzie.",
      },
      {
        q: "Czy można wklejać CV kandydatów do publicznych narzędzi AI?",
        a: "Nie bez odpowiedniej umowy i podstawy prawnej. Dane kandydatów to dane osobowe; potrzebne są zasady przetwarzania, umowa powierzenia z dostawcą i minimalizacja zakresu danych.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Metodologia: opis praktyk pochodzi z procesów rekrutacyjnych prowadzonych przez Positive Recruitment w latach 2025-2026. Odniesienia do AI Act i RODO opisujemy w ujęciu praktycznym dla pracodawcy i nie stanowią porady prawnej.",
  },
  en: {
    title: "AI in IT recruitment: what works and what is risky",
    h1: "AI in IT recruitment 2026: what works, what fails, what is banned",
    metaTitle: "AI in IT Recruitment 2026: Use Cases, Risks, EU AI Act",
    metaDescription:
      "How language models change IT hiring in 2026: where AI shortens the process, where it fails, what the EU AI Act says about high-risk systems, and how candidates use AI.",
    eyebrow: "Insights · AI in hiring",
    summary:
      "Where language models genuinely shorten IT recruitment, where they fail expensively, and what the EU AI Act requires.",
    lead:
      "IT hiring in 2026 runs with language models on both sides: we use them for research and structuring information, candidates use them to write CVs and prepare for interviews. We run searches from Kraków across Poland and CEE, so here is the version without marketing: what works for us, what we deliberately keep manual, and where the law draws a hard line.",
    note:
      "Reflects the situation as of September 2026. AI Act references are framed practically for employers and are not legal advice.",
    readTime: "10 min read",
    takeaways: [
      "AI is good at structuring information: summarising CVs, mapping stacks, drafting interview questions. It is bad at deciding who to hire.",
      "Systems used to screen candidates are high-risk under the EU AI Act - they require human oversight, documentation and candidate disclosure.",
      "Auto-rejecting candidates with no human in the loop is both a legal risk (GDPR Art. 22) and a hiring risk.",
      "Models overrate candidates whose CVs echo the job ad, because they score wording similarity rather than skill - hence the false positives.",
      "More than half the CVs we receive have passed through a language model; judging a 'well-written CV' has lost its predictive value.",
      "Our approach: AI compresses research and preparation, a human always owns the recommendation.",
    ],
    sections: [
      {
        id: "gdzie-dziala",
        eyebrow: "01 / What works",
        title: "Where language models genuinely help in IT hiring",
        blocks: [
          {
            kind: "table",
            caption: "AI use cases across the hiring process - practical verdict",
            head: ["Use case", "Value", "Condition for safe use"],
            rows: [
              ["Summarising and structuring CVs", "High", "A human reads the original before deciding"],
              ["Mapping stacks and synonyms", "High", "Verified by a technical recruiter"],
              ["Drafting interview questions", "High", "Calibrated with the client's engineers"],
              ["Editing ads and outreach", "Medium", "Specifics and salary ranges written by a human"],
              ["Initial matching against requirements", "Medium", "As a sorting hint, never as a rejection filter"],
              ["Assessing competence and rejecting", "Low and risky", "We do not automate this - humans decide"],
              ["Facial, voice or 'culture fit' analysis", "No scientific basis", "Not recommended; high legal and discrimination risk"],
            ],
          },
          {
            kind: "p",
            text: "The split is simple: models organise information that already exists, and invent judgements nobody has verified. Anything that advances or eliminates a candidate stays with a person.",
          },
        ],
      },
      {
        id: "gdzie-zawodzi",
        eyebrow: "02 / What fails",
        title: "Five mistakes language models make when judging candidates",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Wording similarity instead of skill",
                meta: "False positives",
                body: "The model rewards CVs that echo the job ad. Someone who describes the same project in different words drops down the ranking despite stronger experience.",
              },
              {
                title: "Penalising unusual paths",
                meta: "False negatives",
                body: "Career changes, gaps, work at a lesser-known company - anything off-pattern lowers the score with no substantive reason.",
              },
              {
                title: "Fabricated detail",
                meta: "Credibility risk",
                body: "A summary can add a technology or role that never appeared in the CV. Without checking the original, that ends up in the client recommendation.",
              },
              {
                title: "No market context",
                meta: "Wrong salary ranges",
                body: "The model does not know what a senior actually costs in Kraków this quarter or how deep the pool is. That knowledge comes from live processes, not text.",
              },
              {
                title: "Reproducing bias in the data",
                meta: "Discrimination risk",
                body: "A model trained on historical decisions can reproduce their skew, including on gender, age or origin.",
              },
            ],
          },
          {
            kind: "p",
            text: "None of these is theoretical, and they all end the same way: a strong candidate never reaches the shortlist and the company never finds out, because the metrics only show the people who got through.",
          },
        ],
      },
      {
        id: "prawo",
        eyebrow: "03 / The law",
        title: "EU AI Act and GDPR: what is allowed in hiring",
        blocks: [
          {
            kind: "table",
            caption: "Employer obligations when using AI in recruitment",
            head: ["Area", "Rule", "What it means in practice"],
            rows: [
              ["System classification", "Candidate screening is a high-risk use under the AI Act", "Documentation, risk assessment and a usage record are required"],
              ["Human oversight", "Real oversight, not a formal click-through", "A competent person can override or reject the model's output"],
              ["Candidate disclosure", "Candidates know an AI system is in the process", "A short, plain-language note in the ad or first contact"],
              ["Automated decisions", "GDPR restricts solely automated decisions with legal effects", "Rejection without a human in the loop is legally exposed"],
              ["Candidate data", "Data minimisation and a lawful basis", "Do not paste CVs into public tools without a processing agreement"],
              ["Emotion and biometrics", "Emotion recognition in hiring is prohibited", "Analysing facial expression or tone of voice in interviews is not permitted"],
            ],
          },
          {
            kind: "p",
            text: "The simplest compliance test: can you name the person who made the decision and explain the reason to the candidate in two sentences? If the answer is 'the system decided', the process needs fixing, however good the model's output looks.",
          },
        ],
      },
      {
        id: "kandydaci",
        eyebrow: "04 / The other side",
        title: "Candidates use AI too, and it changes the process",
        blocks: [
          {
            kind: "list",
            items: [
              "AI-assisted CVs are clean and tailored to the ad, so a 'good CV' no longer separates candidates.",
              "Cover letters have lost their signal; we replace them with one specific question about a real project.",
              "Unsupervised take-home tasks now say more about the tool than the candidate.",
              "A conversation about real design decisions ('why this approach, what went wrong') remains the hardest thing to fake.",
              "Banning AI in the process is unenforceable; it is more useful to state where it is fine and where we assess independent thinking.",
            ],
          },
          {
            kind: "p",
            text: "In practice we shifted the weight of assessment from artefacts (CV, take-home) to a conversation about specific decisions and their consequences. That is the only stage where experience and a well-worded description of experience look different.",
          },
        ],
      },
      {
        id: "nasze-podejscie",
        eyebrow: "05 / Our approach",
        title: "How we use AI inside the Fit-to-Win Sprint",
        blocks: [
          {
            kind: "table",
            caption: "Division of labour between model and human",
            head: ["Sprint stage", "AI role", "Human role"],
            rows: [
              ["Market map", "Clustering profiles by stack and seniority", "Choosing target companies and segments, checking relevance"],
              ["Candidate outreach", "First draft", "Project context, salary range, a named sender"],
              ["Screening", "Summarising calls and notes", "Judging motivation, fit and real competence"],
              ["Client recommendation", "Organising facts", "The recommendation and its reasoning"],
              ["Candidate feedback", "Draft", "Content and delivery - always from a person"],
            ],
          },
          {
            kind: "p",
            text: "The gain is measurable in time, not in judgement: research that took two days takes half a day, which is how the shortlist lands in 14 days. The recommendation criteria are unchanged and still owned by the person who spoke to the candidate.",
          },
        ],
      },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "Can AI reject candidates on its own?",
        a: "It should not. The EU AI Act treats candidate screening as a high-risk use requiring genuine human oversight, and GDPR restricts solely automated decisions with significant effects. A human must approve and be able to justify a rejection.",
      },
      {
        q: "Do candidates have to be told AI is used in the process?",
        a: "Yes. Candidates should know an AI system is involved, at which stage, and how it influences the decision. A short, plain-language note in the ad or first contact is enough.",
      },
      {
        q: "Is facial or voice analysis allowed in interviews?",
        a: "Emotion recognition in the workplace and in hiring is prohibited in the EU. Regardless of the law, such tools have no sound scientific basis for assessing competence.",
      },
      {
        q: "Is an AI-written CV a red flag?",
        a: "No, it is the norm now. What matters is whether the candidate can talk through the projects described: the decisions they made, what went wrong, and what they would do differently.",
      },
      {
        q: "Does AI shorten IT hiring timelines?",
        a: "Yes, in specific places: research, structuring data and preparing materials. The biggest time savings still come from a disciplined decision process on the company side, not from tooling.",
      },
      {
        q: "Can we paste candidate CVs into public AI tools?",
        a: "Not without a lawful basis and a proper agreement. CVs are personal data, so you need processing rules, a data processing agreement with the vendor and strict data minimisation.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Methodology: practices described come from searches run by Positive Recruitment in 2025-2026. References to the EU AI Act and GDPR are framed practically for employers and are not legal advice.",
  },
  zh: {
    title: "AI 在 IT 招聘中的作用与风险",
    h1: "2026年AI与IT招聘: 有效之处、失效之处与法律红线",
    metaTitle: "2026 AI 招聘: 应用场景、风险与欧盟 AI Act",
    metaDescription:
      "2026年大语言模型如何改变IT招聘: 哪些环节真正提效、哪些环节容易出错、欧盟AI Act对高风险系统的要求，以及候选人如何使用AI。",
    eyebrow: "Insights · AI 招聘",
    summary: "大语言模型在IT招聘中真正提效的环节、代价高昂的失误，以及欧盟 AI Act 的合规要求。",
    lead:
      "2026年的IT招聘，双方都在使用大语言模型: 我们用于市场研究与信息整理，候选人用于撰写简历与面试准备。我们从克拉科夫服务波兰与中东欧市场，以下不谈营销话术，只讲实践: 什么有效、什么我们坚持人工、以及法律在哪里划线。",
    note: "内容基于2026年9月的情况。对 AI Act 的引用面向雇主实践，不构成法律意见。",
    readTime: "10 分钟阅读",
    takeaways: [
      "AI 擅长整理信息: 简历摘要、技术栈映射、面试问题准备; 不擅长决定录用谁。",
      "用于候选人筛选的AI系统在欧盟 AI Act 下属于高风险，需要人工监督、文档与告知义务。",
      "无人工参与的自动淘汰既有法律风险(GDPR 第22条)，也有招聘风险。",
      "模型按用词相似度而非能力打分，容易产生误判。",
      "我们收到的简历中超过一半经过语言模型处理，「简历写得好」已失去预测价值。",
      "我们的做法: AI 压缩研究与准备时间，推荐决策始终由人做出。",
    ],
    sections: [
      {
        id: "gdzie-dziala",
        eyebrow: "01 / 有效之处",
        title: "大语言模型在IT招聘中的真实价值",
        blocks: [
          {
            kind: "table",
            caption: "招聘流程中的AI应用评估",
            head: ["应用场景", "价值", "安全使用条件"],
            rows: [
              ["简历摘要与结构化", "高", "决策前由人阅读原件"],
              ["技术栈与同义词映射", "高", "由技术招聘人员复核"],
              ["面试问题准备", "高", "与客户团队共同校准"],
              ["职位描述与触达文案", "中", "薪资与项目细节由人撰写"],
              ["初步匹配", "中", "仅作排序提示，不作淘汰筛选"],
              ["能力评估与淘汰决策", "低且高风险", "不自动化，由人决定"],
              ["表情、声音与「文化匹配」分析", "缺乏科学依据", "不建议使用，法律与歧视风险高"],
            ],
          },
          { kind: "p", text: "原则很简单: 模型善于整理已有信息，不善于给出无人验证的判断。任何推进或淘汰候选人的动作都由人负责。" },
        ],
      },
      {
        id: "gdzie-zawodzi",
        eyebrow: "02 / 失效之处",
        title: "模型评估候选人时的五类错误",
        blocks: [
          {
            kind: "cards",
            items: [
              { title: "以用词相似度替代能力", meta: "误判通过", body: "复述职位描述用词的简历得分更高，用不同语言描述同类项目的候选人反而排名下降。" },
              { title: "惩罚非典型履历", meta: "误判淘汰", body: "转行、职业空窗、任职于知名度较低的公司，都会无理由降低评分。" },
              { title: "编造细节", meta: "可信度风险", body: "摘要可能补出简历中并不存在的技术或职位，未经核对便进入客户推荐。" },
              { title: "缺乏市场语境", meta: "薪资区间失准", body: "模型不了解本季度克拉科夫资深工程师的真实成本与人才池深度。" },
              { title: "复制数据中的偏见", meta: "歧视风险", body: "基于历史决策训练的模型会复现其中的性别、年龄或籍贯偏差。" },
            ],
          },
          { kind: "p", text: "这些错误的结果相同: 优秀候选人未能进入候选名单，而企业无从察觉，因为统计中只看得到通过的人。" },
        ],
      },
      {
        id: "prawo",
        eyebrow: "03 / 法律",
        title: "AI Act 与 GDPR: 招聘中的合规边界",
        blocks: [
          {
            kind: "table",
            caption: "雇主使用招聘AI的义务",
            head: ["领域", "规则", "实践含义"],
            rows: [
              ["系统分级", "候选人筛选属于高风险应用", "需要文档、风险评估与使用记录"],
              ["人工监督", "需要实质监督", "具备能力的人可推翻模型结论"],
              ["告知候选人", "候选人应知悉AI参与流程", "在职位描述或首次联系中简要说明"],
              ["自动化决策", "GDPR 限制完全自动化的重大决策", "无人工参与的淘汰存在法律风险"],
              ["候选人数据", "最小化与合法基础", "未签署数据处理协议不得将简历粘贴至公共工具"],
              ["情绪与生物识别", "招聘中的情绪识别被禁止", "不得分析面试中的表情或语调"],
            ],
          },
          { kind: "p", text: "最简单的合规检验: 你能否指出做出决定的人，并用两句话向候选人解释理由。若答案是「系统决定的」，流程就需要修改。" },
        ],
      },
      {
        id: "kandydaci",
        eyebrow: "04 / 候选人一侧",
        title: "候选人也在用AI，这改变了流程",
        blocks: [
          {
            kind: "list",
            items: [
              "AI 辅助的简历表达规范且贴合职位描述，「好简历」不再具有区分度。",
              "求职信已失去信息价值，我们改为提出一个关于真实项目的具体问题。",
              "无监督的家庭作业更能反映工具水平，而非候选人水平。",
              "围绕真实技术决策的对话最难伪装。",
              "全面禁止使用AI并不可行，明确哪些环节允许、哪些环节考察独立思考更有意义。",
            ],
          },
          { kind: "p", text: "我们把评估重心从材料转向对具体决策及其后果的对话，这是唯一能区分「经验」与「对经验的良好表述」的环节。" },
        ],
      },
      {
        id: "nasze-podejscie",
        eyebrow: "05 / 我们的做法",
        title: "Fit-to-Win Sprint 中的人机分工",
        blocks: [
          {
            kind: "table",
            caption: "模型与人的分工",
            head: ["阶段", "AI 的角色", "人的角色"],
            rows: [
              ["市场地图", "按技术栈与级别聚类", "选择目标企业与细分，校验相关性"],
              ["候选人触达", "初稿", "项目背景、薪资区间与实名署名"],
              ["初筛", "沟通记录摘要", "判断动机、匹配度与真实能力"],
              ["客户推荐", "整理事实", "作出推荐并说明理由"],
              ["候选人反馈", "草稿", "内容与发送始终由人负责"],
            ],
          },
          { kind: "p", text: "收益体现在时间而非判断上: 原本两天的研究缩短到半天，因此候选名单可在14天内交付。推荐标准不变，仍由与候选人沟通过的人负责。" },
        ],
      },
    ],
    faqTitle: "常见问题",
    faq: [
      { q: "AI 可以自行淘汰候选人吗?", a: "不应如此。AI Act 将候选人筛选视为高风险应用并要求实质人工监督，GDPR 亦限制完全自动化的重大决策。淘汰须由人批准并能够说明理由。" },
      { q: "必须告知候选人使用了AI吗?", a: "需要。应说明AI参与的环节及其对决策的影响，在职位描述或首次联系中简要告知即可。" },
      { q: "面试中可以分析表情或声音吗?", a: "欧盟禁止在职场与招聘中使用情绪识别，且此类工具缺乏可靠科学依据。" },
      { q: "AI 撰写的简历是危险信号吗?", a: "不是，如今这已是常态。关键在于候选人能否讲清项目中的决策、失误与改进思路。" },
      { q: "AI 能缩短招聘周期吗?", a: "能，主要在研究、信息整理与材料准备环节。最大的时间收益仍来自企业端有纪律的决策流程。" },
      { q: "可以把候选人简历粘贴到公共AI工具吗?", a: "在缺乏合法基础与数据处理协议的情况下不可以。简历属于个人数据，需要处理规则与数据最小化。" },
    ],
    cta: ctaZh,
    methodology:
      "方法说明: 相关实践来自 Positive Recruitment 于2025-2026年开展的招聘流程。对 AI Act 与 GDPR 的引用面向雇主实践，不构成法律意见。",
  },
};
